import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/store/toastStore';
import {
  addKbQuizQuestion,
  deleteKbQuiz,
  deleteKbQuizQuestion,
  getKbQuiz,
  kbQueryKeys,
  updateKbQuiz,
  updateKbQuizQuestion,
  type SaveKbQuizQuestionRequest,
} from '@/services/knowledge-base.service';

interface QuestionForm {
  prompt: string;
  questionType: 'single' | 'multi';
  choices: Array<{ label: string; isCorrect: boolean }>;
}

const EMPTY_QUESTION: QuestionForm = {
  prompt: '',
  questionType: 'single',
  choices: [
    { label: '', isCorrect: false },
    { label: '', isCorrect: false },
  ],
};

export function QuizBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const quizId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showAddQ, setShowAddQ] = useState(false);
  const [questionForm, setQuestionForm] = useState<QuestionForm>(EMPTY_QUESTION);
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [title, setTitle] = useState('');
  const [passThreshold, setPassThreshold] = useState(80);
  const [status, setStatus] = useState<'active' | 'archived' | 'draft'>('active');

  const quizQuery = useQuery({
    queryKey: kbQueryKeys.quiz(quizId),
    queryFn: () => getKbQuiz(quizId),
    enabled: quizId > 0,
  });

  useEffect(() => {
    if (quizQuery.data) {
      setTitle(quizQuery.data.title);
      setPassThreshold(quizQuery.data.passThreshold);
      setStatus((quizQuery.data.status as typeof status) ?? 'active');
    }
  }, [quizQuery.data]);

  const saveQuizMutation = useMutation({
    mutationFn: () => updateKbQuiz(quizId, { title: title.trim(), passThreshold, status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.quizzes() });
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.quiz(quizId) });
      toast.success('Quiz saved');
    },
    onError: () => toast.error('Failed to save'),
  });

  const addQuestionMutation = useMutation({
    mutationFn: (payload: SaveKbQuizQuestionRequest) => addKbQuizQuestion(quizId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.quiz(quizId) });
      toast.success('Question added');
      setShowAddQ(false);
      setQuestionForm(EMPTY_QUESTION);
    },
    onError: () => toast.error('Failed to add question'),
  });

  const updateQuestionMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: SaveKbQuizQuestionRequest }) =>
      updateKbQuizQuestion(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.quiz(quizId) });
      toast.success('Question updated');
      setShowAddQ(false);
      setEditingQuestionId(null);
      setQuestionForm(EMPTY_QUESTION);
    },
    onError: () => toast.error('Failed to update question'),
  });

  const deleteQuestionMutation = useMutation({
    mutationFn: (id: number) => deleteKbQuizQuestion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.quiz(quizId) });
    },
  });

  const deleteQuizMutation = useMutation({
    mutationFn: () => deleteKbQuiz(quizId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.quizzes() });
      toast.success('Quiz deleted');
      navigate('..');
    },
  });

  function openEditQuestion(qid: number) {
    const q = quizQuery.data?.questions.find((q) => q.kbQuizQuestionId === qid);
    if (!q) return;
    setEditingQuestionId(qid);
    setQuestionForm({
      prompt: q.prompt,
      questionType: q.questionType === 'multi' ? 'multi' : 'single',
      choices: q.choices.map((c) => ({ label: c.label, isCorrect: c.isCorrect ?? false })),
    });
    setShowAddQ(true);
  }

  function saveQuestion() {
    const payload: SaveKbQuizQuestionRequest = {
      prompt: questionForm.prompt.trim(),
      questionType: questionForm.questionType,
      choices: questionForm.choices
        .filter((c) => c.label.trim() !== '')
        .map((c) => ({ label: c.label.trim(), isCorrect: c.isCorrect })),
    };
    if (!payload.prompt) {
      toast.error('Prompt is required');
      return;
    }
    if (payload.choices.length < 2) {
      toast.error('At least two choices are required');
      return;
    }
    if (!payload.choices.some((c) => c.isCorrect)) {
      toast.error('Mark at least one choice as correct');
      return;
    }
    if (editingQuestionId) {
      updateQuestionMutation.mutate({ id: editingQuestionId, payload });
    } else {
      addQuestionMutation.mutate(payload);
    }
  }

  if (quizQuery.isLoading) {
    return <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading...</div>;
  }
  if (!quizQuery.data) {
    return <div className="rounded-lg border border-slate-200 bg-white p-6">Quiz not found.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Link to=".." className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft className="h-4 w-4" />
          Back to quizzes
        </Link>
        <div className="flex gap-2">
          <Button variant="danger" leftIcon={<Trash2 className="h-4 w-4" />} onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
          <Button onClick={() => saveQuizMutation.mutate()} loading={saveQuizMutation.isPending}>
            Save
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <div className="text-xs uppercase tracking-wide text-slate-500">
          Quiz on {quizQuery.data.targetType}: {quizQuery.data.targetTitle || `#${quizQuery.data.targetId}`}
        </div>
        <Input label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="grid gap-3 md:grid-cols-2">
          <FormField label={`Pass threshold (${passThreshold}%)`}>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={passThreshold}
              onChange={(e) => setPassThreshold(Number(e.target.value))}
              className="w-full"
            />
          </FormField>
          <FormField label="Status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
              className="form-input-base"
            >
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </FormField>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
          <h3 className="text-sm font-semibold text-slate-900">Questions</h3>
          <Button
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => {
              setEditingQuestionId(null);
              setQuestionForm(EMPTY_QUESTION);
              setShowAddQ(true);
            }}
          >
            Add Question
          </Button>
        </div>

        {quizQuery.data.questions.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-slate-500">No questions yet.</div>
        ) : (
          <ol className="divide-y divide-slate-100">
            {quizQuery.data.questions.map((q, idx) => (
              <li key={q.kbQuizQuestionId} className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900">
                      {idx + 1}. {q.prompt}
                    </div>
                    <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                      {q.choices.map((c) => (
                        <li key={c.kbQuizChoiceId} className="flex items-center gap-2">
                          <span>•</span>
                          <span>{c.label}</span>
                          {c.isCorrect && (
                            <Badge variant="success" size="sm">
                              Correct
                            </Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex flex-shrink-0 gap-1">
                    <Button size="xs" variant="ghost" onClick={() => openEditQuestion(q.kbQuizQuestionId)}>
                      Edit
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => deleteQuestionMutation.mutate(q.kbQuizQuestionId)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <Modal
        isOpen={showAddQ}
        onClose={() => {
          setShowAddQ(false);
          setEditingQuestionId(null);
          setQuestionForm(EMPTY_QUESTION);
        }}
        title={editingQuestionId ? 'Edit Question' : 'Add Question'}
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddQ(false);
                setEditingQuestionId(null);
                setQuestionForm(EMPTY_QUESTION);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={saveQuestion}
              loading={addQuestionMutation.isPending || updateQuestionMutation.isPending}
            >
              {editingQuestionId ? 'Save' : 'Add'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Textarea
            label="Question prompt"
            required
            value={questionForm.prompt}
            onChange={(e) => setQuestionForm((f) => ({ ...f, prompt: e.target.value }))}
            rows={3}
          />
          <FormField label="Question type">
            <select
              value={questionForm.questionType}
              onChange={(e) =>
                setQuestionForm((f) => ({ ...f, questionType: e.target.value as 'single' | 'multi' }))
              }
              className="form-input-base"
            >
              <option value="single">Single answer</option>
              <option value="multi">Multiple answers</option>
            </select>
          </FormField>
          <FormField label="Choices">
            <div className="space-y-2">
              {questionForm.choices.map((choice, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={choice.isCorrect}
                    onChange={() =>
                      setQuestionForm((f) => ({
                        ...f,
                        choices: f.choices.map((c, idx) =>
                          idx === i ? { ...c, isCorrect: !c.isCorrect } : c,
                        ),
                      }))
                    }
                    aria-label={`Choice ${i + 1} is correct`}
                  />
                  <input
                    type="text"
                    value={choice.label}
                    onChange={(e) =>
                      setQuestionForm((f) => ({
                        ...f,
                        choices: f.choices.map((c, idx) =>
                          idx === i ? { ...c, label: e.target.value } : c,
                        ),
                      }))
                    }
                    placeholder={`Choice ${i + 1}`}
                    className="form-input-base flex-1"
                  />
                  {questionForm.choices.length > 2 && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() =>
                        setQuestionForm((f) => ({
                          ...f,
                          choices: f.choices.filter((_, idx) => idx !== i),
                        }))
                      }
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() =>
                  setQuestionForm((f) => ({
                    ...f,
                    choices: [...f.choices, { label: '', isCorrect: false }],
                  }))
                }
              >
                Add choice
              </Button>
            </div>
          </FormField>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => deleteQuizMutation.mutate()}
        title="Delete quiz?"
        message="This deletes the quiz, its questions, and all attempt records."
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
